import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  GetStaticProps,
  GetStaticPaths,
  InferGetStaticPropsType,
} from 'next';
import { useRouter } from 'next/router';
import ErrorPage from 'next/error';
import getUrlByEnv from '@utils/get-url-by-env';
import getNav from '@framework/api/operations/get-nav';
import { useTranslation as t } from '@utils/hooks';
import { ROUTE_SUBSCRIPTION, REVALIDATE_VALUES } from '@constants';
import Head from '@components/Head';
import Layout from '@components/Layout';
import ContentWrapper from '@components/ContentWrapper';
import SectionWrapper from '@components/SectionWrapper';
import { ProductNav, OuterWrapper } from '@components/PDP/ProductNav';
import Downloads from '@components/PDP/Downloads';
import FAQSection from '@components/FAQSection';
import SubscriptionPdp from 'fragile/components/SubscriptionPdp';
import { SubscriptionProductCollection, FragileApiConfig } from 'fragile/types';
import { FragileApi } from 'fragile/api/wrapper';
import fragileGetSubscriptionProduct from 'fragile/api/operations/get-subscription';
import fragileGetAllSubscriptions from 'fragile/api/operations/get-all-subscriptions';
import SingleItemTableSpecs from '@components/CollectionPage/ComparisonTable/Table/SingleItemTableSpecs';
import Overview from '@components/PDP/Overview/Overview';
import YotpoWidget from '@components/YotpoReviews/YotpoWidget';
import * as detailsStyles from '@components/CollectionPage/ComparisonTable/Table/styles';
import { getSessionStorage } from '@utils/sessionStorage';
import ServiceBadge from '../../components/PDP/ServiceBadge';
import { ConditionOption } from '../../fragile/components/PdpConditionSelector/PdpConditionSelector';

const revalidate = +process.env.NEXT_PUBLIC_PRODUCT_REVALIDATION_TIME || 30;

// Define a new type for the data structure
type SubscriptionDataWithConditions = {
  [ConditionOption.LIKE_NEW]: SubscriptionProductCollection;
  [ConditionOption.GUARANTEED_NEW]: SubscriptionProductCollection;
};

export const getStaticProps: GetStaticProps = async ({
  locale,
  params,
  preview,
}) => {
  const baseUrl = getUrlByEnv();
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug;

  const fragileApi = FragileApi.getInstance();
  const parts = await fragileApi.getParts();
  const config = await fragileApi.getConfig();

  const allSubscriptions = await fragileGetAllSubscriptions(preview);

  // Find the current subscription product by slug
  const currentSubscription = allSubscriptions.find(
    product => product.slug === slug
  );

  // If product not found and not a CSS map file, return 404
  if (!currentSubscription && !slug.endsWith('.css.map')) {
    return {
      notFound: true,
      revalidate: REVALIDATE_VALUES.notFound,
    };
  }

  // Create an array to store all subscription products with condition options
  const dataWithConditions = await Promise.all(
    allSubscriptions.map(async product => {
      // Version 1: Like-New data (minimum specs, no parts)
      const likeNewData = await fragileGetSubscriptionProduct(
        fragileApi,
        locale,
        product.fragileProduct,
        preview
      );

      // For the current or adjacent product (matching slug), fetch detailed data for both conditions
      if (
        currentSubscription.collection[0].name === product.collection[0].name
      ) {
        // Version 2: Guaranteed New data (exact parts)
        const guaranteedNewData = await fragileGetSubscriptionProduct(
          fragileApi,
          locale,
          product.fragileProduct,
          preview,
          parts
        );

        // We only need the case parts for the like new condition
        // as this isn't in Dato
        const partsKeys = Object.keys(guaranteedNewData.parts.parts);
        const partsCaseOnly = { parts: {} };
        partsKeys.forEach(key => {
          partsCaseOnly.parts[key] = {
            CASES: guaranteedNewData.parts.parts[key].CASES,
          };
        });

        return {
          [ConditionOption.LIKE_NEW]: {
            ...likeNewData,
            parts: partsCaseOnly,
          },
          [ConditionOption.GUARANTEED_NEW]: guaranteedNewData,
        };
      }
      // Use the same data for both conditions to avoid extra API calls
      // Only need basic info for the tier selector
      // monthlyPrice and fromCms.collection[0].name
      return {
        [ConditionOption.LIKE_NEW]: {
          monthlyPrice: likeNewData.monthlyPrice,
          fromCms: {
            collection: likeNewData.fromCms.collection.map(c => ({
              name: c.name,
            })),
            slug: likeNewData.fromCms.slug,
          },
        },
        [ConditionOption.GUARANTEED_NEW]: null,
      };
    })
  );

  const nav = await getNav({ slug: 'main', locale, preview });

  return {
    props: {
      preview: preview || false,
      baseUrl,
      dataWithConditions,
      config,
      nav,
      slug,
    },
    revalidate,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const allSubscriptions = await fragileGetAllSubscriptions();

  return {
    paths:
      allSubscriptions?.map(
        product => `/${ROUTE_SUBSCRIPTION}/${product.slug}`
      ) || [],
    fallback: 'blocking',
  };
};

const SubscriptionProductPage: InferGetStaticPropsType<
  typeof getStaticProps
> = ({
  baseUrl,
  slug,
  dataWithConditions,
  config,
}: {
  baseUrl: string;
  slug: string;
  dataWithConditions: SubscriptionDataWithConditions[];
  config: FragileApiConfig;
}) => {
  const router = useRouter();
  const { query } = router;

  const headerRef = useRef(null);
  const overviewRef = useRef(null);

  const SUBSCRIPTION_AGREEMENT = t('subscription_agreement');

  // Find the current product data by matching the slug
  const currentProductData = dataWithConditions.find(
    product => product[ConditionOption.LIKE_NEW].fromCms.slug === slug
  );

  // Get the initial product version (standard by default)
  const initialUpgradeOption = currentProductData?.[ConditionOption.LIKE_NEW]
    .products[0]?.upgrades[0]?.options[0]
    ? currentProductData[
        ConditionOption.LIKE_NEW
      ].products[0].upgrades[0].options.sort(
        (a, b) => a.displayOrder - b.displayOrder
      )[0]
    : null;

  const [selectedUpgradeOption, setUpgradeOption] =
    useState(initialUpgradeOption);

  const fragileConfig = config.perProductConfig[selectedUpgradeOption.name];
  const isOutOfStockConditionMap = useMemo(
    () => ({
      [ConditionOption.LIKE_NEW]: fragileConfig?.isOutOfRefurbishedStock,
      [ConditionOption.GUARANTEED_NEW]:
        fragileConfig?.isOutOfNewStock ||
        !!selectedUpgradeOption?.fromCms?.isOutOfStock,
    }),
    [fragileConfig, selectedUpgradeOption]
  );

  const [selectedConditionOption, setConditionOption] =
    useState<ConditionOption>(
      isOutOfStockConditionMap[ConditionOption.LIKE_NEW]
        ? ConditionOption.GUARANTEED_NEW
        : ConditionOption.LIKE_NEW
    );

  // Use useEffect to set the condition option after initial render (client-side only)
  useEffect(() => {
    // This effect only runs once on mount
    const checkPrevPath = (): void => {
      const isGuaranteedNewOOS =
        isOutOfStockConditionMap[ConditionOption.GUARANTEED_NEW];
      if (isGuaranteedNewOOS) {
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('new-selected') !== null) {
        setConditionOption(ConditionOption.GUARANTEED_NEW);
        return;
      }
      const prevPath = getSessionStorage('prevPath');
      const isComingFromProductPage =
        prevPath && prevPath.match(/\/product\/player-[\w-]+/);
      if (isComingFromProductPage) {
        setConditionOption(ConditionOption.GUARANTEED_NEW);
      }
    };

    // Check the previous path immediately
    checkPrevPath();

    // Use requestAnimationFrame to ensure the code runs after browser paint
    const animationId = window.requestAnimationFrame(() => {
      checkPrevPath();
    });

    return () => {
      window.cancelAnimationFrame(animationId);
    };

    // Empty dependency array ensures this effect only runs once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Simple effect to handle both initialization and condition changes
  useEffect(() => {
    if (!currentProductData) return;

    // Get the current condition's product data
    const newProduct = currentProductData[selectedConditionOption];

    const currentName = selectedUpgradeOption.name;

    // Search through products and upgrades to find a matching option
    let matchingOption = null;

    // Use array methods with proper type safety
    newProduct.products.some(product =>
      product.upgrades.some(upgrade => {
        const match = upgrade.options.find(
          option => option.name === currentName
        );
        if (match) {
          matchingOption = match;
          return true;
        }
        return false;
      })
    );

    if (matchingOption) {
      setUpgradeOption(matchingOption);
    } else {
      setUpgradeOption(initialUpgradeOption);
    }
  }, [
    currentProductData,
    selectedUpgradeOption,
    initialUpgradeOption,
    selectedConditionOption,
  ]);

  if (
    !currentProductData ||
    !currentProductData[ConditionOption.LIKE_NEW].fromCms.slug
  ) {
    return <ErrorPage statusCode={404} />;
  }

  // Get the current product based on the selected condition
  const currentProduct = currentProductData[selectedConditionOption];

  if (!currentProduct) {
    return null;
  }

  return (
    <>
      <Head
        baseUrl={baseUrl}
        canonical={`${baseUrl}/${ROUTE_SUBSCRIPTION}/${query.slug}`}
        cmsData={currentProduct.fromCms.seo}
      />
      <div id="overview_anchor" className="relative">
        {/*
         | BLOCK: Breadcrumbs
         */}

        {Array.isArray(currentProduct?.fromCms.collection) &&
          currentProduct?.fromCms.collection?.length > 0 && (
            <OuterWrapper>
              <div ref={headerRef}>
                <ProductNav
                  collectionData={currentProduct.fromCms.collection}
                  productName={currentProduct.fromCms.name}
                />
              </div>
            </OuterWrapper>
          )}

        {/*
         | BLOCK: Above The Fold Content
         */}

        <ContentWrapper>
          <div ref={overviewRef}>
            <SubscriptionPdp
              selectedUpgradeOption={selectedUpgradeOption}
              setUpgradeOption={setUpgradeOption}
              selectedConditionOption={selectedConditionOption}
              setConditionOption={setConditionOption}
              subscriptionProductName={currentProduct.fromCms.name}
              shortDescription={currentProduct.fromCms.shortDescription}
              images={
                selectedUpgradeOption?.fromCms?.images ??
                currentProduct.fromCms.images
              }
              price={`$${Math.floor(
                currentProduct.monthlyPrice / 100
              )} per month`}
              subscriptionProduct={currentProduct}
              allSubscriptionProducts={dataWithConditions}
              isOutOfStockConditionMap={isOutOfStockConditionMap}
            />
          </div>
        </ContentWrapper>

        <div className="space-y-12 lg:space-y-20 mb-20 mt-8">
          {/*
           | BLOCK: Overview Content
           */}

          {currentProduct?.fromCms?.overviewContent?.length > 0 && (
            <Overview
              overviewContent={currentProduct.fromCms.overviewContent}
            />
          )}

          {/*
           | BLOCK: Faq
           */}

          {Array.isArray(currentProduct?.fromCms?.productFaqs) &&
            currentProduct?.fromCms?.productFaqs.length > 0 && (
              <FAQSection
                noWrapperSpacing
                titleSize="small"
                title={t('subscription_faq')}
                titleAlignment="Left"
                faqQuestions={currentProduct.fromCms?.productFaqs}
              />
            )}

          {/*
           | BLOCK: Rental Details
           */}

          <SectionWrapper noSpacing>
            <ContentWrapper>
              <h2 className="h5 mb-6 md:mb-9 lg:mb-12">
                {t('subscription_details')}
              </h2>

              <SingleItemTableSpecs
                decoratedSpecs={Object.keys(
                  currentProduct.fromCms.subscriptionDetails ?? {}
                ).map(detailName => ({
                  label: detailName,
                  value: currentProduct.fromCms.subscriptionDetails[detailName],
                }))}
              />
              <div className={detailsStyles.specsWrapper}>
                <p className={detailsStyles.specCategory}>
                  {t('subscription_pdp_more_details')}
                </p>
                <div className={`${detailsStyles.specValueDetail} mt-1.5`}>
                  <ServiceBadge
                    data={{
                      copy: SUBSCRIPTION_AGREEMENT,
                      externalLink: {
                        url: 'https://fragile.co/legal/subscription-agreement',
                      },
                      iconName: 'IconUrl',
                    }}
                  />
                </div>
              </div>
            </ContentWrapper>
          </SectionWrapper>

          {/*
           | BLOCK: Reviews
           */}

          <SectionWrapper noSpacing>
            <ContentWrapper>
              <h2
                className="h5 mb-6 md:mb-9 lg:mb-12 scroll-mt-[12rem]"
                id="reviews"
              >
                {t('reviews_label')}
              </h2>
              <YotpoWidget
                type="reviews"
                productName={currentProduct.fromCms.name}
                id={currentProduct.fromCms.yotpoProductId}
                slug={currentProduct.fromCms.slug}
                description={currentProduct.fromCms.shortDescription}
              />
            </ContentWrapper>
          </SectionWrapper>

          {/*
           | BLOCK: Downloads
           */}

          <Downloads downloadsData={currentProduct.fromCms.downloads ?? []} />
        </div>
      </div>
    </>
  );
};

SubscriptionProductPage.Layout = Layout;

export default SubscriptionProductPage;
